import { cn } from "@v1/ui/utils";
import { ORDERED_LIST_NEST_STYLE_CLASSES } from "./rich-editor/ordered-list-nest-classes";

export type MarkdownHeadingScale = "default" | "compact";

const FIRST_HEADING_RESET =
  "first:[&_h1]:mt-0 first:[&_h2]:mt-0 first:[&_h3]:mt-0";

/**
 * Strong h1 → h2 → h3 ladder: large pixel steps + weight steps so each level
 * is unmistakable (plan markdown + agent prompt editor).
 */
export function markdownHeadingScaleClasses(
  scale: MarkdownHeadingScale,
): string {
  if (scale === "compact") {
    return cn(
      FIRST_HEADING_RESET,
      "[&_h1]:mt-3.5 [&_h1]:mb-2 [&_h1]:text-[22px] [&_h1]:font-bold [&_h1]:leading-tight [&_h1]:tracking-tight",
      "[&_h2]:mt-3 [&_h2]:mb-1.5 [&_h2]:text-[17px] [&_h2]:font-semibold [&_h2]:leading-snug",
      "[&_h3]:mt-2 [&_h3]:mb-1 [&_h3]:text-[14px] [&_h3]:font-normal [&_h3]:leading-snug",
    );
  }

  return cn(
    FIRST_HEADING_RESET,
    "[&_h1]:mt-6 [&_h1]:mb-2.5 [&_h1]:text-[28px] [&_h1]:font-bold [&_h1]:leading-tight [&_h1]:tracking-tight",
    "[&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:text-[21px] [&_h2]:font-semibold [&_h2]:leading-snug",
    "[&_h3]:mt-3.5 [&_h3]:mb-1 [&_h3]:text-[16px] [&_h3]:font-normal [&_h3]:leading-snug",
  );
}

export const markdownEditorHeadingColorClasses: string = cn(
  "[&_h1]:text-primary",
  "[&_h2]:text-primary/85",
  "[&_h3]:text-primary/65",
);

export const markdownPlanHeadingColorClassesDefault: string = cn(
  "[&_h1]:text-primary",
  "[&_h2]:text-primary/85",
  "[&_h3]:text-primary/65",
);

export const markdownPlanHeadingColorClassesCompact: string = cn(
  "[&_h1]:text-[#0f172a] dark:[&_h1]:text-[#f2efe8]",
  "[&_h2]:text-[#0f172a]/90 dark:[&_h2]:text-[#f2efe8]/90",
  "[&_h3]:text-[#475569] dark:[&_h3]:text-[#c9c2b4]",
);

/**
 * Body rules for every markdown surface that is meant to read like a page:
 * paragraphs, lists, inline marks, links, code, quotes.
 *
 * Single source on purpose. Copying this block into an editor is how the
 * agent-prompt editor ended up with pages' bullets but its own link and font
 * rules — the two drifted without anyone touching them. Surfaces still pick
 * their own heading scale and font size around it; the body stays shared.
 */
export const markdownBodyProseClasses: string = cn(
  "[&_p]:mb-1.5 last:[&_p]:mb-0",
  "[&_ul]:ml-4 [&_ul]:list-disc [&_ul]:mb-2 last:[&_ul]:mb-0",
  "[&_ol]:ml-4 [&_ol]:list-decimal [&_ol]:mb-2 last:[&_ol]:mb-0",
  ORDERED_LIST_NEST_STYLE_CLASSES,
  "[&_li]:mt-0.5 [&_strong]:font-semibold",
  "[&_a]:text-action-500 [&_a]:underline [&_a]:cursor-pointer",
  "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[11px]",
  "[&_blockquote]:border-l-2 [&_blockquote]:border-primary/20 [&_blockquote]:pl-3 [&_blockquote]:text-primary/60",
);

/** Wissensseiten-Editor + Markdown-Vorschau — same heading ladder as agent prompt sections. */
export const pageEditorProseClasses: string = cn(
  "page-editor-prose max-w-none text-[14px] leading-relaxed text-primary/80",
  markdownHeadingScaleClasses("default"),
  markdownEditorHeadingColorClasses,
  markdownBodyProseClasses,
);
